/**
 * A report PDF described as data: text, bullets and tables placed in points on
 * a Letter page. Each report builds one of these from numbers already on the
 * page (dailyReportPdf.ts, scorecardPdf.ts, ...) and renderPdf.ts draws it, so
 * the builders are plain functions a test can read without a PDF library.
 */

export type Rgb = [number, number, number];

/** The two families jsPDF carries without embedding: Helvetica stands in for Arial, Times for Times New Roman. */
export type FontFamily = "helvetica" | "times";

export interface TextStyle {
  font: FontFamily;
  style: "normal" | "bold" | "italic";
  size: number;
  color: Rgb;
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface CellSpec {
  text: string;
  /** Overrides of the table's text style for this cell. */
  style?: Partial<TextStyle>;
  colSpan?: number;
  fill?: Rgb;
  align?: "left" | "center" | "right";
  padding?: Partial<Padding>;
  /** Rule widths per side, overriding the table's; 0 leaves that side open. */
  rules?: Partial<Padding>;
}

export interface TableSpec {
  /** Left edge, in points. */
  x: number;
  /** One per column, in points. */
  widths: number[];
  head: CellSpec[][];
  body: CellSpec[][];
  /** The text of every cell unless the cell says otherwise. */
  text: TextStyle;
  headText: Partial<TextStyle>;
  headFill?: Rgb;
  lineColor: Rgb;
  lineWidth: number;
  padding: Padding;
  headHeight?: number;
  rowHeight?: number;
  valign: "top" | "middle";
  /** "firstPage": a table that runs onto the next page does not repeat its header, as in the team's report. */
  showHead: "everyPage" | "firstPage";
}

export type Block =
  | { kind: "text"; text: string; style: TextStyle; x: number; gapBefore: number; lineHeight: number; maxWidth: number }
  | {
      kind: "bullet";
      text: string;
      style: TextStyle;
      /** Where the round bullet sits, and where its text starts. */
      bulletX: number;
      bulletSize: number;
      x: number;
      gapBefore: number;
      lineHeight: number;
      maxWidth: number;
    }
  | { kind: "table"; table: TableSpec; gapBefore: number }
  | { kind: "pageBreak" };

export interface PdfDoc {
  fileName: string;
  orientation: "portrait" | "landscape";
  /** Points from each edge of a Letter page. */
  margin: Padding;
  blocks: Block[];
  /** "Page 1 of 2"-style line in the bottom margin, or none (the daily report has none). */
  footer: string | null;
}

/** Letter, in points. */
export const PAGE = { portrait: { width: 612, height: 792 }, landscape: { width: 792, height: 612 } };

/** The colours of the team's report (App Daily Report 28Oct2026.pdf). */
export const INK = {
  black: [0, 0, 0] as Rgb,
  /** Day headings, targets and the app's numbers. */
  blue: [0x4f, 0x81, 0xbd] as Rgb,
  /** The second and third section headings. */
  brightBlue: [0x4a, 0x86, 0xe8] as Rgb,
  /** A target in the funnel table. */
  darkBlue: [0x36, 0x5f, 0x91] as Rgb,
  /** The funnel and activity tables' rules. */
  rule: [0x95, 0xb3, 0xd7] as Rgb,
  grey: [0x66, 0x66, 0x66] as Rgb,
  darkGrey: [0x59, 0x59, 0x59] as Rgb,
  /** "Not yet Available". */
  magenta: [0xff, 0x00, 0xff] as Rgb,
  white: [0xff, 0xff, 0xff] as Rgb,
  /** For notes and footers; not in the team's report. */
  muted: [0x6b, 0x6b, 0x6b] as Rgb,
  good: [0x00, 0x63, 0x00] as Rgb,
  bad: [0xd0, 0x3b, 0x3b] as Rgb,
};

export const text = (font: FontFamily, style: TextStyle["style"], size: number, color: Rgb): TextStyle => ({ font, style, size, color });

/** 28 → "28th", 1 → "1st", 12 → "12th". */
export function ordinal(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  return `${day}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[day % 10] ?? "th"}`;
}

const parseDay = (day: string) => {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date));
};

/** "2026-10-28" → "28th Oct 2026", as the team's report dates itself. */
export function reportDate(day: string): string {
  const date = parseDay(day);
  return `${ordinal(date.getUTCDate())} ${date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })} ${date.getUTCFullYear()}`;
}

/** "2026-10-28" → "28 Oct", the report's column heading. */
export function dayHeading(day: string): string {
  return parseDay(day).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

/** "Oct 29, 2026, 9:14 AM", for "Generated" lines. */
export function generatedAt(now: Date = new Date()): string {
  return now.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
